import localeUtils from 'utils/LanguageLocale';
import dateUtils from 'utils/Date';
import DateEN from 'utils/locales/Date_en_US';
import DateFR from 'utils/locales/Date_fr_CA';
import contentConstants from 'constants/content';

const { BIHUB_SIDS } = contentConstants;

const getDateText = localeUtils.getLocalisedDataFromResource(DateEN, DateFR);

export const BI_PROGRAM_FAQS_URL = '/beauty/loyalty-program';

// Activity type codes
export const ACTIVITY_TYPE_CODE = {
    GAME_POINT_CREDIT: 'GAME_POINT_CREDIT',
    GAME_POINT_DEBIT: 'GAME_POINT_DEBIT'
};

// Beauty Insider activity icon sources
export const ACTIVITY_ICON_SRC = {
    cashApplied: '/img/ufe/icons/points-cash.svg',
    gamification: '/img/ufe/bi-gamification-icon.svg'
};

export const isActivityGroupingEnabled = () => {
    if (localeUtils.isCanada()) {
        return Sephora.configurationSettings.loyalty?.enableCreativeRefreshP1_CA;
    }

    return Sephora.configurationSettings.loyalty?.enableCreativeRefreshP1_US;
};

export const isCreativeRefreshPhase2Enabled = () => {
    if (!isActivityGroupingEnabled()) {
        return false;
    }

    if (localeUtils.isCanada()) {
        return Sephora.configurationSettings.loyalty?.creativeRefreshP2_CA?.isEnabled;
    }

    return Sephora.configurationSettings.loyalty?.creativeRefreshP2_US?.isEnabled;
};

export const filterNewRewardsSectionSkuList = content => {
    if (!Array.isArray(content)) {
        return [];
    }

    const newRewardsItem = content.find(item => item.sid === BIHUB_SIDS.NEW_REWARDS && Array.isArray(item.skuList));

    if (!newRewardsItem) {
        return content;
    }

    const filteredSkuList = newRewardsItem.skuList.filter(sku => sku.isComingSoonTreatment || !sku.isOutOfStock);

    if (filteredSkuList.length === newRewardsItem.skuList.length) {
        return content;
    }

    return content.map(item => (item.sid === BIHUB_SIDS.NEW_REWARDS ? { ...item, skuList: filteredSkuList } : item));
};

// Gamification Activity (BI Challenge)
export const isGamificationActivity = activityTypeCode => {
    return activityTypeCode === ACTIVITY_TYPE_CODE.GAME_POINT_CREDIT || activityTypeCode === ACTIVITY_TYPE_CODE.GAME_POINT_DEBIT;
};

/**
 * Formats a currency value for the appropriate locale.
 * For the French locale, moves the dollar sign to the end (e.g. "$20" → "20 $").
 * Returns a placeholder when no value is provided.
 *
 * @param {string} value - Currency string to format (e.g. "$20").
 * @param {boolean} isFrench - Whether to apply French Canadian formatting.
 * @returns {string} Formatted currency string.
 */
export const formatValue = (value, isFrench) => {
    if (!value) {
        return isFrench ? '-- $' : '$--';
    }

    // Value comes as "$20" or similar, convert for French
    if (isFrench) {
        // Remove $ and add it after the number
        const amount = value.replace('$', '');

        return `${amount} $`;
    }

    return value;
};

/**
 * Formats the expiration date to display as "Aug 1" (EN) or "1 août" (FR)
 * @param {string} dateString - ISO date string (e.g., "2026-06-01T23:59:59-07:00")
 * @param {boolean} isFrench - Whether to format for French locale
 * @returns {string} Formatted date string
 */
export const formatExpirationDate = (dateString, isFrench) => {
    const locale = isFrench ? 'fr-CA' : 'en-US';
    const options = { month: 'short', day: 'numeric', timeZone: 'UTC' };
    const dateMatch = dateString && dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!dateMatch) {
        return '';
    }

    const [, year, month, day] = dateMatch;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

    return date.toLocaleDateString(locale, options);
};

/**
 * Formats expiration date using basket's date parsing logic for consistency
 * @param {string} expirationDate - ISO date string (e.g., "2026-08-04T23:59:59-07:00")
 * @param {boolean} isFrench - Whether to format for French locale
 * @returns {string} Formatted date like "Aug 5" or "5 août"
 */
export const formatExpirationLocalized = (expirationDate, isFrench) => {
    const mmddyyDate = dateUtils.getDateInMMDDYYFormat(expirationDate);

    if (!mmddyyDate) {
        return '';
    }

    const [month, day] = mmddyyDate.split('/');
    const monthIndex = Number(month) - 1; // Convert to 0-based index

    const monthNames = getDateText('SHORTENED_MONTHS');
    const monthName = monthNames[monthIndex];

    return isFrench ? `${Number(day)} ${monthName.toLowerCase()}` : `${monthName} ${Number(day)}`;
};
